import { useEffect, useRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Renders the mixed state used by a "select all" box over a partial selection. */
  indeterminate?: boolean;
}

/**
 * A native checkbox, styled.
 *
 * `indeterminate` is a DOM property rather than an attribute, so it has to be
 * assigned imperatively - React will not set it from JSX. Keeping the native
 * input means keyboard, screen-reader and form behaviour come for free.
 */
export function Checkbox({ indeterminate = false, className, ...props }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={cn(
        'size-4 shrink-0 cursor-pointer rounded border-line text-brand-500',
        'accent-brand-500 focus:ring-2 focus:ring-brand-100',
        className,
      )}
      {...props}
    />
  );
}
